import React, { useEffect, useRef, useState } from "react";
import style from "./uploadPhotos.module.scss";
import { getBase64Move } from "../../../../utils/productsHelper";
import { fixBaseCodeMove } from "../../../../utils/productImageHelper";
import { Button } from "../../../primary/Button/button";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../static/images/spinner.svg";
import { useParams } from "react-router-dom";
import { uploadAdditionalPhotosAsync } from "../../../../app/patientSlice";
import constants from '../../../../constants/constants.json';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const UploadPhotos = ({ show, onClose, assessmentNumber }) => {
  const dispatch = useDispatch();
  const { patientId } = useParams();
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const dropRef = useRef(null);

  const uploadAdditionalPhotos = useSelector((state) => state.patient.uploadAdditionalPhotos);

  const validTypes = ["image/jpeg", "image/jpg", "image/png"];

  useEffect(() => {
    setTimeout(() => {
      setFileError("");
    }, 5000);
  }, [dispatch, fileError]);

  const handleFiles = (allFiles) => {
    if(allFiles?.length > 10) {
      setFileError(constants.photos.maxPhotoError);
      return;
    }
    const invalidFiles = allFiles.filter((file) => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setFileError(constants.photos.fileExtensionError);
      return;
    }

    const bigFile = allFiles.filter((file) => file.size > MAX_FILE_SIZE);
    if (bigFile.length > 0) {
      setFileError(constants.photos.maxPhotoSizeError);
      return;
    }

    setFileError("");

    setFiles((prev) => {
      const isExist = new Set(prev.map((f) => `${f.file.name}-${f.file.size}`));

      const newFiles = allFiles
        .filter((file) => !isExist.has(`${file.name}-${file.size}`))
        .map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        }));

      return [...prev, ...newFiles];
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f?.file?.name !== name));
  };

  const handleUpload = async () => {
    if (files.length > 0) {
      const base64Images = await Promise.all(
        files.map(async (f) => {
          const base64 = await getBase64Move(f.file);
          return fixBaseCodeMove(base64);
        })
      );

      const data = { base64_image: base64Images };
      const payload = { ...data, patient_id: patientId, assessment_id: assessmentNumber };
      dispatch(uploadAdditionalPhotosAsync(payload)).then(() => {
        onClose();
      });

      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    dropRef.current.classList.add(style.drag_over);
  };

  const handleDragLeave = () => {
    dropRef.current.classList.remove(style.drag_over);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dropRef.current.classList.remove(style.drag_over);
    if (e.dataTransfer.files) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <>
      {show && (
        <div className={`modal fade show d-block ${style.upload_photos}`} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title m-0" style={{ fontFamily: "open_sansbold" }}>
                  Upload Additional Photos
                </h5>
                <button type="button" className="close" onClick={onClose} aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body">
                {fileError && <div className={`alert alert-danger p-2 ${style.alert}`}>{fileError}</div>}
                <p className={`${style.image_format_hint} mb-2`}>Max file size: 5MB</p>
                <p className={`${style.image_format_hint} mb-2`}>Supported image format (JPG, JPEG, PNG)</p>
                <div
                  ref={dropRef}
                  className={style.upload_photo_wrapper}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <p>Drag & Drop Photos or</p>
                  <input className="d-none" id="photoUpload" type="file" accept=".jpg,.jpeg,.png" multiple onChange={handleFileChange} />
                  {uploadAdditionalPhotos?.loading ? (
                    <img
                      src={Loader}
                      alt="loading"
                      loading="lazy"
                      className={style.loaderIcon}
                    />
                  ) : <label htmlFor="photoUpload" className={style.custom_file_btn}>
                  <i className="fa fa-folder-open mr-2"></i> Browse Photo(s)
                </label>}
                </div>

                {files.length > 0 && (
                  <ul className={style.file_list}>
                    {files.map((f) => (
                      <li key={f.file.name} className={style.file_item}>
                        <div className={style.thumb_wrapper}>
                          <img src={f.preview} alt={f.file.name} className={style.thumb_img} />
                        </div>
                        <span className={style.file_name}>{f.file.name}</span>
                        <Button
                          type="button"
                          classes={{ className: style.remove_btn }}
                          children={<>&times;</>}
                          onClick={() => removeFile(f.file.name)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="modal-footer">
                <Button type="button" classes={{ className: `btn btn-secondary ${style.cancel_btn}` }} value="Cancel" onClick={onClose} />
                <Button
                  type="button"
                  classes={{ className: `btn btn-primary ${style.upload_btn}` }}
                  onClick={handleUpload}
                  disabled={files.length === 0}
                >
                  Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
